import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useSearchParams, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Unit } from '@/types'
import {
  ArrowLeft,
  Save,
  GraduationCap,
  Plus,
  Trash2,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Edit,
  Volume2
} from 'lucide-react'

interface ExamFormData {
  unit_id: string
  title: string
  description: string
  passing_score: number
  time_limit: number
  max_attempts: number
  is_active: boolean
}

interface QuestionFormData {
  id?: string
  question: string
  audio_url: string
  options: string[]
  correct_answer: string
  points: number
  order_num: number
}

export default function ExamForm() {
  const { examId } = useParams()
  const [searchParams] = useSearchParams()
  const unitIdFromUrl = searchParams.get('unit')
  const navigate = useNavigate()
  const location = useLocation()
  const isEditing = Boolean(examId) && examId !== 'new'
  const isViewMode = isEditing && !location.pathname.endsWith('/edit')

  const [units, setUnits] = useState<Unit[]>([])
  const [formData, setFormData] = useState<ExamFormData>({
    unit_id: unitIdFromUrl || '',
    title: '',
    description: '',
    passing_score: 70,
    time_limit: 30,
    max_attempts: 3,
    is_active: true
  })
  const [questions, setQuestions] = useState<QuestionFormData[]>([])
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadUnits()
    if (isEditing) {
      loadExam()
    }
  }, [examId])

  const loadUnits = async () => {
    try {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .order('order_num', { ascending: true })

      if (error) throw error
      setUnits(data || [])
    } catch (error) {
      console.error('Error loading units:', error)
    }
  }

  const loadExam = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('unit_exams')
        .select(`
          *,
          questions:exam_questions(
            *,
            options:exam_question_options(*)
          )
        `)
        .eq('id', examId as string)
        .single()

      if (error) throw error

      const examData = data as any

      setFormData({
        unit_id: examData.unit_id,
        title: examData.title_es || examData.title_fr || examData.title || '',
        description: examData.description_es || examData.description_fr || examData.description || '',
        passing_score: examData.passing_score,
        time_limit: examData.time_limit_minutes ?? examData.time_limit ?? 30,
        max_attempts: examData.max_attempts,
        is_active: examData.is_active
      })

      if (examData.questions && examData.questions.length > 0) {
        setQuestions(
          examData.questions
            .sort((a: any, b: any) => (a.order_index ?? a.order_num ?? 0) - (b.order_index ?? b.order_num ?? 0))
            .map((q: any) => {
              const opts = (q.options || []).sort((a: any, b: any) => (a.order_index || 0) - (b.order_index || 0))
              return {
                id: q.id,
                question: q.question_es || q.question_fr || q.question || '',
                audio_url: q.audio_url || '',
                options: opts.length > 0
                  ? opts.map((o: any) => o.content_es || o.content_fr || '')
                  : q.options || ['', '', '', ''],
                correct_answer: opts.find((o: any) => o.is_correct)?.content_es
                  || opts.find((o: any) => o.is_correct)?.content_fr
                  || q.correct_answer || '',
                points: q.points,
                order_num: q.order_index ?? q.order_num ?? 0
              }
            })
        )
      }
    } catch (error) {
      console.error('Error loading exam:', error)
      setError('Error al cargar el examen')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSaving(true)

    try {
      let examIdToUse = examId

      const examPayload = {
        unit_id: formData.unit_id,
        title_es: formData.title,
        title_fr: formData.title,
        description_es: formData.description || null,
        description_fr: formData.description || null,
        passing_score: formData.passing_score,
        time_limit_minutes: formData.time_limit,
        max_attempts: formData.max_attempts,
        is_active: formData.is_active
      }

      if (isEditing) {
        const { error } = await (supabase
          .from('unit_exams') as any)
          .update({ ...examPayload, updated_at: new Date().toISOString() })
          .eq('id', examId as string)

        if (error) throw error
      } else {
        const { data, error } = await (supabase
          .from('unit_exams') as any)
          .insert(examPayload)
          .select()
          .single()

        if (error) throw error
        examIdToUse = (data as any).id
      }

      // Guardar preguntas
      if (questions.length > 0 && examIdToUse) {
        // Eliminar preguntas existentes (cascade eliminará las opciones)
        if (isEditing) {
          await supabase
            .from('exam_questions')
            .delete()
            .eq('exam_id', examIdToUse)
        }

        // Insertar preguntas una por una para obtener sus IDs
        for (let i = 0; i < questions.length; i++) {
          const q = questions[i]
          const { data: insertedQ, error: qError } = await (supabase
            .from('exam_questions') as any)
            .insert({
              exam_id: examIdToUse,
              question_es: q.question,
              question_fr: q.question,
              audio_url: q.audio_url?.trim() || null,
              skill: q.audio_url?.trim() ? 'listening' : 'reading',
              type: 'multiple_choice',
              points: q.points,
              order_index: i + 1,
              is_active: true
            })
            .select()
            .single()

          if (qError) throw qError

          // Insertar opciones de la pregunta
          const validOptions = q.options.filter(o => o.trim() !== '')
          if (validOptions.length > 0) {
            const optionsToInsert = validOptions.map((opt, oIndex) => ({
              question_id: insertedQ.id,
              content_es: opt,
              content_fr: opt,
              is_correct: opt === q.correct_answer,
              order_index: oIndex + 1
            }))

            const { error: optError } = await (supabase
              .from('exam_question_options') as any)
              .insert(optionsToInsert)

            if (optError) throw optError
          }
        }
      }

      navigate('/admin/exams')
    } catch (error: any) {
      console.error('Error saving exam:', error)
      setError(error.message || 'Error al guardar el examen')
    } finally {
      setIsSaving(false)
    }
  }

  const addQuestion = () => {
    const newQuestion: QuestionFormData = {
      question: '',
      audio_url: '',
      options: ['', '', '', ''],
      correct_answer: '',
      points: 10,
      order_num: questions.length + 1
    }
    setQuestions([...questions, newQuestion])
    setExpandedQuestion(questions.length)
  }

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index))
    if (expandedQuestion === index) {
      setExpandedQuestion(null)
    }
  }

  const updateQuestion = (index: number, field: keyof QuestionFormData, value: any) => {
    setQuestions(questions.map((q, i) =>
      i === index ? { ...q, [field]: value } : q
    ))
  }

  const updateQuestionOption = (questionIndex: number, optionIndex: number, value: string) => {
    setQuestions(questions.map((q, i) => {
      if (i !== questionIndex) return q
      const newOptions = [...q.options]
      newOptions[optionIndex] = value
      return { ...q, options: newOptions }
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/exams"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isViewMode ? 'Visualizar Examen' : (isEditing ? 'Editar Examen' : 'Nuevo Examen')}
            </h1>
            <p className="text-gray-600">
              {isViewMode ? 'Vista previa del examen' : (isEditing ? 'Modifica los datos del examen' : 'Crea un nuevo examen de unidad')}
            </p>
          </div>
        </div>
        {isViewMode && (
          <Link
            to={`/admin/exams/${examId}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Edit className="w-5 h-5" />
            Editar
          </Link>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-100">
              <GraduationCap className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Información del Examen</h2>
          </div>

          <div>
            <label htmlFor="exam-unit" className="block text-sm font-medium text-gray-700 mb-1">
              Unidad
            </label>
            <select id="exam-unit" value={formData.unit_id}
              onChange={(e) => setFormData(prev => ({ ...prev, unit_id: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              disabled={isViewMode}
            >
              <option value="">Seleccionar unidad...</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>
                  Unidad {unit.order_num}: {unit.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="exam-title" className="block text-sm font-medium text-gray-700 mb-1">
              Título del Examen
            </label>
            <input id="exam-title" type="text" value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ej: Examen de Unidad 1"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              disabled={isViewMode}
            />
          </div>

          <div>
            <label htmlFor="exam-description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción (opcional)
            </label>
            <textarea id="exam-description" value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Instrucciones para el estudiante..."
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="exam-passing-score" className="block text-sm font-medium text-gray-700 mb-1">
                Puntaje para Aprobar (%)
              </label>
              <input id="exam-passing-score" type="number"
                min="0"
                max="100"
                value={formData.passing_score}
                onChange={(e) => setFormData(prev => ({ ...prev, passing_score: Number.parseInt(e.target.value) || 70 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
            <div>
              <label htmlFor="exam-time-limit"  className="block text-sm font-medium text-gray-700 mb-1">
                Tiempo Limite (minutos)
              </label>
              <input id="exam-time-limit" type="number" 
                min="5"
                max="180"
                value={formData.time_limit}
                onChange={(e) => setFormData(prev => ({ ...prev, time_limit: Number.parseInt(e.target.value) || 30 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
            <div>
              <label htmlFor="exam-max-attempts" className="block text-sm font-medium text-gray-700 mb-1">
                Intentos Máximos
              </label>
              <input id="exam-max-attempts" type="number"
                min="1"
                max="10"
                value={formData.max_attempts}
                onChange={(e) => setFormData(prev => ({ ...prev, max_attempts: Number.parseInt(e.target.value) || 3 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="w-4 h-4 text-[#0055A4] border-gray-300 rounded focus:ring-[#0055A4] disabled:cursor-not-allowed"
              disabled={isViewMode}
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Examen activo (disponible para estudiantes)
            </label>
          </div>
        </div>

        {/* Questions */}
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#0055A4]/10">
                <HelpCircle className="w-5 h-5 text-[#0055A4]" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Preguntas ({questions.length})
              </h2>
            </div>
            {!isViewMode && (
              <button
                type="button"
                onClick={addQuestion}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Agregar Pregunta
              </button>
            )}
          </div>

          <div className="space-y-4">
            {questions.map((question, qIndex) => (
              <div key={qIndex} className="border rounded-lg overflow-hidden">
                {/* Question Header */}
                <button
                  type="button"
                  onClick={() => setExpandedQuestion(expandedQuestion === qIndex ? null : qIndex)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium text-gray-900">
                    Pregunta {qIndex + 1}: {question.question || '(Sin título)'}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{question.points} pts</span>
                    {expandedQuestion === qIndex ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Question Content */}
                {expandedQuestion === qIndex && (
                  <div className="p-4 space-y-4">
                    <div>
                      <label htmlFor={`question-text-${qIndex}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Pregunta
                      </label>
                      <input id={`question-text-${qIndex}`} type="text" value={question.question}
                        onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                        placeholder="Escribe la pregunta..."
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                      />
                    </div>

                    <div>
                      <label htmlFor={`question-audio-${qIndex}`} className="block text-sm font-medium text-gray-700 mb-1">
                        <span className="inline-flex items-center gap-1.5">
                          <Volume2 className="w-4 h-4 text-[#0055A4]" />
                          Audio URL (opcional - para preguntas de escucha)
                        </span>
                      </label>
                     <input id={`question-audio-${qIndex}`} type="url" value={question.audio_url}
                        onChange={(e) => updateQuestion(qIndex, 'audio_url', e.target.value)}
                        placeholder="https://ejemplo.com/audio.mp3"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                      />
                      {question.audio_url && (
                        <p className="text-xs text-[#0055A4] mt-1">
                          Esta pregunta se marcará automáticamente como tipo "listening"
                        </p>
                      )}
                    </div>

                    <div>
                      <span className="block text-sm font-medium text-gray-700 mb-2">
                        Opciones
                      </span>
                      <div className="space-y-2">
                        {question.options.map((option, oIndex) => (
                          <div key={oIndex} className="flex items-center gap-2">
                            <span className="text-sm text-gray-500 w-6">
                              {String.fromCodePoint(65 + oIndex)}.
                            </span>
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => updateQuestionOption(qIndex, oIndex, e.target.value)}
                              placeholder={`Opción ${String.fromCodePoint(65 + oIndex)}`}
                              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor={`question-answer-${qIndex}`} className="block text-sm font-medium text-gray-700 mb-1">
                          Respuesta Correcta
                        </label>
                        <select id={`question-answer-${qIndex}`} value={question.correct_answer}
                          onChange={(e) => updateQuestion(qIndex, 'correct_answer', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                        >
                          <option value="">Seleccionar...</option>
                          {question.options.map((option, oIndex) => (
                            option.trim() && (
                              <option key={oIndex} value={option}>
                                {String.fromCodePoint(65 + oIndex)}. {option}
                              </option>
                            )
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`question-points-${qIndex}`} className="block text-sm font-medium text-gray-700 mb-1">
                          Puntos
                        </label>
                        <input id={`question-points-${qIndex}`} type="number" value={question.points} 
                          onChange={(e) => updateQuestion(qIndex, 'points', Number.parseInt(e.target.value) || 10)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
                        />
                      </div>
                    </div>

                    {!isViewMode && (
                      <div className="flex justify-end pt-2">
                        <button
                          type="button"
                          onClick={() => removeQuestion(qIndex)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Eliminar Pregunta
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {questions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <HelpCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>No hay preguntas aún</p>
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={addQuestion}
                    className="text-[#0055A4] hover:underline text-sm mt-1"
                  >
                    Agregar primera pregunta
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-3">
            <Link
              to="/admin/exams"
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={isSaving || !formData.unit_id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Examen')}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}
